import { objectMapValues } from "@redotech/util/object";
import {
  AttributionCauseEventType,
  AttributionTouchpoint,
  type AttributionSettings,
} from "./attribution-settings";

/**
 * Product that owns a touchpoint. Useful for isolating product-specific attribution settings pages.
 */
export enum AttributionProduct {
  MARKETING = "marketing",
}

/**
 * The product each touchpoint belongs to. Keyed by {@link AttributionTouchpoint}
 * so a new touchpoint must declare its product before it compiles.
 */
const TOUCHPOINT_PRODUCT: Record<AttributionTouchpoint, AttributionProduct> = {
  [AttributionTouchpoint.RECOVER]: AttributionProduct.MARKETING,
  [AttributionTouchpoint.EMAIL]: AttributionProduct.MARKETING,
  [AttributionTouchpoint.SMS]: AttributionProduct.MARKETING,
};

/** Touchpoints owned by a given product, in enum declaration order. */
export function touchpointsForProduct(
  product: AttributionProduct,
): AttributionTouchpoint[] {
  return Object.values(AttributionTouchpoint).filter(
    (touchpoint) => TOUCHPOINT_PRODUCT[touchpoint] === product,
  );
}

/**
 * The touchpoint each cause event belongs to. Typed as a Record over every
 * {@link AttributionCauseEventType}, so a new cause event can't compile until
 * it's assigned to a touchpoint (and maps to exactly one).
 */
const CAUSE_EVENT_TOUCHPOINT: Record<
  AttributionCauseEventType,
  AttributionTouchpoint
> = {
  [AttributionCauseEventType.EMAIL_SEND]: AttributionTouchpoint.EMAIL,
  [AttributionCauseEventType.EMAIL_DELIVERED]: AttributionTouchpoint.EMAIL,
  [AttributionCauseEventType.EMAIL_OPEN]: AttributionTouchpoint.EMAIL,
  [AttributionCauseEventType.EMAIL_CLICK]: AttributionTouchpoint.EMAIL,
  [AttributionCauseEventType.SMS_SEND]: AttributionTouchpoint.SMS,
  [AttributionCauseEventType.SMS_CLICK]: AttributionTouchpoint.SMS,
  [AttributionCauseEventType.RECOVER_MESSAGE_SENT]:
    AttributionTouchpoint.RECOVER,
  [AttributionCauseEventType.RECOVER_MESSAGE_RECEIVED]:
    AttributionTouchpoint.RECOVER,
  [AttributionCauseEventType.RECOVER_CLICK]: AttributionTouchpoint.RECOVER,
};

/** Cause events that make up a touchpoint, in cause-event declaration order. */
export function touchpointCauseEvents(
  touchpoint: AttributionTouchpoint,
): AttributionCauseEventType[] {
  return Object.values(AttributionCauseEventType).filter(
    (causeEvent) => CAUSE_EVENT_TOUCHPOINT[causeEvent] === touchpoint,
  );
}

/** Pass to {@link restrictToTouchpoints} for the holistic (unrestricted) view. */
export const ALL_ATTRIBUTION_TOUCHPOINTS = Object.values(AttributionTouchpoint);

/**
 * Team settings overlaid so only `keep` touchpoints stay in play — every other
 * touchpoint's inferred causes and direct config are switched off.
 */
export function restrictToTouchpoints(
  settings: AttributionSettings,
  keep: readonly AttributionTouchpoint[],
): AttributionSettings {
  const excluded = ALL_ATTRIBUTION_TOUCHPOINTS.filter(
    (tp) => !keep.includes(tp),
  );
  const offCauses = new Set<AttributionCauseEventType>(
    excluded.flatMap(touchpointCauseEvents),
  );
  const offTouchpoints = new Set<AttributionTouchpoint>(excluded);
  return objectMapValues(settings, (config) => ({
    ...config,
    inferred: objectMapValues(config.inferred, (cfg, cause) =>
      cfg && offCauses.has(cause) ? { ...cfg, enabled: false } : cfg,
    ),
    direct: objectMapValues(config.direct, (cfg, touchpoint) =>
      cfg && offTouchpoints.has(touchpoint) ? { ...cfg, enabled: false } : cfg,
    ),
  }));
}

/**
 * The cause events each touchpoint's DIRECT attribution draws from: the send
 * (the message that carried the code) and the click (same code, same
 * touchpoint id — so it dedupes with the send, and stands in when the send
 * event wasn't recorded).
 */
export const TOUCHPOINT_DIRECT_EVENTS: Record<
  AttributionTouchpoint,
  AttributionCauseEventType[]
> = {
  [AttributionTouchpoint.RECOVER]: [
    AttributionCauseEventType.RECOVER_MESSAGE_SENT,
    AttributionCauseEventType.RECOVER_CLICK,
  ],
  [AttributionTouchpoint.EMAIL]: [
    AttributionCauseEventType.EMAIL_SEND,
    AttributionCauseEventType.EMAIL_CLICK,
  ],
  [AttributionTouchpoint.SMS]: [
    AttributionCauseEventType.SMS_SEND,
    AttributionCauseEventType.SMS_CLICK,
  ],
};
